'use client';
import { __rest } from '../../node_modules/tslib/tslib.es6.js';
import { jsx, jsxs } from 'react/jsx-runtime';
import FocusTrap from 'focus-trap-react';
import { motion, AnimatePresence } from 'framer-motion';
import * as React from 'react';
import { RemoveScroll } from 'react-remove-scroll';
import { StickyScrollContainer } from 'react-unstuck';
import styled from 'styled-components';
import { fade } from '../../animations/fade.js';
import { zIndexUtil } from '../../helpers/zIndexUtil.js';
import { withTitan } from '../../hocs/withTitan/withTitan.js';
import { useGreaterThan } from '../../hooks/useGreaterThan.js';
import { media } from '../../mixins/media.js';
import { scrollDirection } from '../../mixins/scrollDirection.js';
import { ContentWithSidebar } from '../ContentWithSidebar/ContentWithSidebar.js';
import { Dimmer } from '../Dimmer/Dimmer.js';
import { Logo } from '../Logo/Logo.js';
import { ModalCloseButton } from '../Modal/ModalCloseButton.js';
import { Portal } from '../Portal/Portal.js';
import { FullscreenLayoutContent } from './FullscreenLayoutContent.js';
import { FullscreenLayoutProvider } from './FullscreenLayoutContext.js';
import { FullscreenLayoutSidebar } from './FullscreenLayoutSidebar.js';
import { useCloseFullscreenLayout } from './hooks/useCloseFullscreenLayout.js';
import { useFullscreenLayoutCloseable } from './hooks/useFullscreenLayoutCloseable.js';

const StyledPageContainer = styled.div.withConfig({
  componentId: "sc-1jazyct-0"
})(["background:", ";height:100vh;overflow:hidden;"], ({
  theme
}) => theme.components.fullscreenLayout.backgroundColor);
const StyledCloseButton = styled(ModalCloseButton).withConfig({
  componentId: "sc-1jazyct-1"
})(["margin:", ";z-index:2;"], ({
  theme
}) => `${theme.components.fullscreenLayout.closeIconMargin} ` + `${theme.components.fullscreenLayout.closeIconMargin} ` + `${theme.components.fullscreenLayout.closeIconMargin} ` + 'auto');
const StyledLogo = styled(Logo).withConfig({
  componentId: "sc-1jazyct-2"
})(["margin:", ";z-index:2;"], ({
  theme
}) => theme.components.fullscreenLayout.logoMargin);
const StyledAnimatedDimmer = styled(Dimmer).withConfig({
  componentId: "sc-1jazyct-3"
})(["background:", ";"], ({
  theme
}) => theme.components.fullscreenLayout.backgroundColor);
const StyledAnimatedModal = styled(motion.div).withConfig({
  shouldForwardProp: prop => !['zIndex'].includes(prop)
}).withConfig({
  componentId: "sc-1jazyct-4"
})(["position:fixed;background:", ";inset:0;height:100vh;overflow:hidden;z-index:", ";"], ({
  theme
}) => theme.components.fullscreenLayout.backgroundColor, ({
  zIndex
}) => zIndex);
const StyledScroller = styled(StickyScrollContainer).withConfig({
  componentId: "sc-1jazyct-5"
})(["height:100%;", ";"], scrollDirection.vertical());
const StyledHeader = styled.div.withConfig({
  componentId: "sc-1jazyct-6"
})(["display:flex;position:relative;top:0;width:100%;z-index:", ";background:", ";", ";"], zIndexUtil.stickyInModelDefaultZIndex, ({
  theme
}) => theme.components.fullscreenLayout.backgroundColor, media.greaterThan('s')`
    position: fixed;
  `);
const FullscreenLayoutPage = /*#__PURE__*/withTitan(({
  brandName,
  sidebar,
  footer,
  logoComponent,
  children,
  testId = 'fullscreenLayoutPage'
}) => {
  const isLarge = useGreaterThan('s');
  const isCloseable = useFullscreenLayoutCloseable();
  const close = useCloseFullscreenLayout();
  return jsx(StyledPageContainer, {
    children: jsxs(StyledScroller, {
      children: [jsxs(StyledHeader, {
        children: [(brandName || logoComponent) && jsx(StyledLogo, {
          brandName: brandName,
          svg: logoComponent
        }), isCloseable && jsx(StyledCloseButton, {
          "data-test-id": `${testId}CloseButton`,
          size: 24,
          onClick: close
        })]
      }), jsx(ContentWithSidebar, {
        contentWidth: {
          xs: 1,
          s: 'stretch'
        },
        isSticky: isLarge,
        sidebar: sidebar && jsx(FullscreenLayoutSidebar, {
          children: sidebar
        }),
        sidebarWidth: {
          xs: 1,
          s: 360,
          l: 400,
          xl: 440
        },
        children: jsx(FullscreenLayoutContent, {
          footer: footer,
          testId: testId,
          children: children
        })
      })]
    })
  });
}, {
  name: 'FullscreenLayoutPage'
});
const FullscreenLayoutModal = /*#__PURE__*/withTitan((_a, ref) => {
  var {
      zIndex = zIndexUtil.fullscreenLayout,
      duration = 200,
      testId = 'fullscreenLayout',
      onDimmerClose
    } = _a,
    rest = __rest(_a, ["zIndex", "duration", "testId", "onDimmerClose"]);
  const isCloseable = useFullscreenLayoutCloseable();
  return jsx(RemoveScroll, {
    className: "scroll",
    children: jsx(StyledAnimatedDimmer, Object.assign({
      ref: ref,
      zIndex: zIndex - 10,
      onClose: isCloseable ? onDimmerClose : undefined
    }, fade.in, {
      children: jsx(FocusTrap, {
        focusTrapOptions: {
          clickOutsideDeactivates: true,
          fallbackFocus: 'body'
        },
        children: jsx(StyledAnimatedModal, Object.assign({
          "aria-modal": "true",
          "data-test-id": testId,
          role: "dialog",
          transition: {
            ease: 'easeOut',
            duration: duration / 1000
          },
          zIndex: zIndex
        }, fade.in, {
          children: jsx(FullscreenLayoutPage, Object.assign({
            isModal: true
          }, rest))
        }))
      })
    }), "dimmer")
  });
}, {
  name: 'FullscreenLayoutModal'
});
const FullscreenLayout = /*#__PURE__*/withTitan(_a => {
  var {
      isShown,
      isModal = false,
      isCloseable = true,
      onClose,
      zIndex = zIndexUtil.fullscreenLayout,
      duration = 200
    } = _a,
    rest = __rest(_a, ["isShown", "isModal", "isCloseable", "onClose", "zIndex", "duration"]);
  const overlayNode = React.useRef(null);
  const handleDimmerClose = e => {
    if (!onClose) return;
    // If no dimmer event this means we pressed ESC
    if (e.type !== 'click') return onClose();
    // Because content is inside dimmer we need to indentify dimmer element click specifically
    if (overlayNode.current === e.target) {
      onClose();
    }
  };
  return jsx(FullscreenLayoutProvider, {
    close: onClose,
    isCloseable: isCloseable,
    children: isModal ? jsx(AnimatePresence, {
      children: isShown && jsx(Portal, {
        children: jsx(FullscreenLayoutModal, Object.assign({
          ref: overlayNode,
          duration: duration,
          zIndex: zIndex - 10,
          onDimmerClose: handleDimmerClose
        }, fade.in, rest), "dimmer")
      })
    }) : isShown && jsx(FullscreenLayoutPage, Object.assign({}, rest))
  });
}, {
  name: 'FullscreenLayout'
});

export { FullscreenLayout };
//# sourceMappingURL=FullscreenLayout.js.map
